import { useRef, useState } from "react";
import { Upload, FileText, Image as ImageIcon, X, Loader2, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const ACCEPT = "image/*,application/pdf,application/zip,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.json,.xml,.csv,.md";
const MAX_BYTES = 25 * 1024 * 1024;
const MAX_FILES = 10;
const MAX_TOTAL = 50 * 1024 * 1024;

/**
 * Drag-and-drop file picker.
 * Two modes:
 *  • mode="upload-only" (default) — uploads via POST /api/files/upload and returns metadata via onChange.
 *    Used in the Submit Idea modal where the parent entity does not yet exist.
 *  • mode="attach"      — uploads via the entity-specific endpoint (entityUrl prop), so the entity
 *    record itself ends up with the attachment (idea or product).
 *
 * `value` is an array of FileAttachment metadata objects.
 * onChange(nextValue) is called whenever a file is added/removed.
 */
export default function FileDropzone({
    value = [],
    onChange,
    mode = "upload-only",
    entityUrl,
    canDelete = true,
    testIdPrefix = "file-dropzone",
    label = "Drop files here or click to browse",
    helpText,
}) {
    const inputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState([]); // [{ tempId, name, progress }]
    const [error, setError] = useState("");

    const totalBytes = value.reduce((sum, f) => sum + (f.size || 0), 0);
    const remainingFiles = Math.max(0, MAX_FILES - value.length);
    const remainingBytes = Math.max(0, MAX_TOTAL - totalBytes);

    const handleFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        if (files.length > remainingFiles) {
            setError(`Only ${remainingFiles} more file slot(s) available (max ${MAX_FILES}).`);
            return;
        }
        setError("");

        for (const file of files) {
            if (file.size > MAX_BYTES) {
                setError(`'${file.name}' exceeds the 25 MB per-file cap.`);
                continue;
            }
            if (file.size > remainingBytes) {
                setError(`Total upload would exceed 50 MB cap.`);
                continue;
            }
            const tempId = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
            setUploading((prev) => [...prev, { tempId, name: file.name, progress: 0 }]);
            try {
                const fd = new FormData();
                fd.append("file", file);
                const url = mode === "attach" && entityUrl ? entityUrl : "/files/upload";
                const { data } = await api.post(url, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: (evt) => {
                        if (!evt.total) return;
                        const pct = Math.round((evt.loaded / evt.total) * 100);
                        setUploading((prev) => prev.map((u) => u.tempId === tempId ? { ...u, progress: pct } : u));
                    },
                });
                if (mode === "attach") {
                    // Backend returned the FULL updated entity — its file_attachments list is canonical.
                    onChange(data.file_attachments || []);
                } else {
                    onChange([...(value || []), data]);
                }
            } catch (err) {
                const msg = formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Upload failed";
                setError(msg);
                toast.error(msg);
            } finally {
                setUploading((prev) => prev.filter((u) => u.tempId !== tempId));
            }
        }
    };

    const removeFile = async (file) => {
        if (mode === "attach" && entityUrl) {
            // Detach via DELETE on entity-specific endpoint
            try {
                const detachUrl = `${entityUrl}/${file.file_id}`;
                const { data } = await api.delete(detachUrl);
                onChange(data.file_attachments || []);
            } catch (err) {
                toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
            }
        } else {
            // Upload-only mode: just drop from the local array. The blob is orphaned in
            // GridFS until garbage collection, which is acceptable for this scope.
            onChange(value.filter((f) => f.file_id !== file.file_id));
        }
    };

    const onDrop = async (e) => {
        e.preventDefault();
        setDragOver(false);
        await handleFiles(e.dataTransfer.files);
    };

    return (
        <div data-testid={testIdPrefix} className="space-y-2">
            <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => remainingFiles > 0 && inputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
                data-testid={`${testIdPrefix}-zone`}
                className={`border-2 border-dashed transition-colors px-5 py-8 text-center cursor-pointer ${
                    dragOver
                        ? "border-signal bg-signal/10"
                        : "border-[#2A2A2A] bg-jet hover:border-snow-muted"
                } ${remainingFiles === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
            >
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept={ACCEPT}
                    className="hidden"
                    data-testid={`${testIdPrefix}-input`}
                    onChange={(e) => handleFiles(e.target.files)}
                />
                <Upload className={`w-6 h-6 mx-auto ${dragOver ? "text-signal" : "text-snow-muted"}`} />
                <div className="mt-2 text-sm text-snow font-mono">{label}</div>
                <div className="text-[11px] text-snow-muted mt-1 font-mono">
                    {helpText || `Max ${MAX_FILES} files, 25 MB each, 50 MB total. Images, PDFs, docs, archives.`}
                </div>
                <div className="text-[10px] text-snow-muted mt-2 font-mono uppercase tracking-wider tabular-nums">
                    {value.length}/{MAX_FILES} files · {formatBytes(totalBytes)}/{formatBytes(MAX_TOTAL)}
                </div>
            </div>

            {error && (
                <div className="border border-red-500/40 bg-red-500/5 text-red-400 px-3 py-2 text-xs font-mono flex items-center gap-2" data-testid={`${testIdPrefix}-error`}>
                    <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                    {error}
                </div>
            )}

            {(value.length > 0 || uploading.length > 0) && (
                <ul className="border border-[#2A2A2A] bg-jet-elev1 divide-y divide-[#2A2A2A]" data-testid={`${testIdPrefix}-list`}>
                    {value.map((f) => (
                        <li key={f.file_id} className="px-3 py-2 flex items-center gap-3 text-xs font-mono" data-testid={`${testIdPrefix}-item-${f.file_id}`}>
                            <FileIcon contentType={f.content_type} />
                            <div className="flex-1 min-w-0">
                                <div className="truncate text-snow">{f.filename}</div>
                                <div className="text-[10px] text-snow-muted uppercase tracking-wider tabular-nums">
                                    {formatBytes(f.size)} · {f.content_type || "binary"}
                                </div>
                            </div>
                            {canDelete && (
                                <button
                                    type="button"
                                    onClick={() => removeFile(f)}
                                    className="text-snow-muted hover:text-red-400 p-1"
                                    data-testid={`${testIdPrefix}-remove-${f.file_id}`}
                                    title="Remove"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            )}
                        </li>
                    ))}
                    {uploading.map((u) => (
                        <li key={u.tempId} className="px-3 py-2 flex items-center gap-3 text-xs font-mono">
                            <Loader2 className="w-4 h-4 text-signal animate-spin flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="truncate text-snow-dim">{u.name}</div>
                                <div className="h-1 bg-jet-elev2 mt-1 overflow-hidden">
                                    <div className="h-full bg-signal transition-all" style={{ width: `${u.progress}%` }} />
                                </div>
                            </div>
                            <span className="text-[10px] text-snow-muted tabular-nums">{u.progress}%</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function FileIcon({ contentType }) {
    if ((contentType || "").startsWith("image/")) return <ImageIcon className="w-4 h-4 text-snow-muted flex-shrink-0" />;
    return <FileText className="w-4 h-4 text-snow-muted flex-shrink-0" />;
}

export function formatBytes(b) {
    if (!b) return "0 B";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
    return `${(b / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export function buildFileDownloadUrl(file_id) {
    // Use REACT_APP_BACKEND_URL so deep-links work the same way as the API client.
    const base = process.env.REACT_APP_BACKEND_URL || "";
    // Build Prompt #18 — append ?token=<jwt> from localStorage. The browser's
    // <img>, <iframe>, and direct <a href> requests do NOT send our Bearer header
    // and on cross-origin custom-domain deployments the httpOnly cookie can't flow
    // either. The query-token lane in the backend (download_file) accepts this as
    // an equivalent third auth source.
    let token = null;
    try {
        token = window.localStorage.getItem("kitt_access_token");
    } catch {
        /* ignore */
    }
    const url = `${base}/api/files/${file_id}`;
    return token ? `${url}?token=${encodeURIComponent(token)}` : url;
}
